

import React, { useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { FaFilePdf } from "react-icons/fa";

const ReportDownload = ({ users, title, filters = {} }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  // Logic: 4 records on Page 1, 5 records on others
  const getPages = (data) => {
    if (!data || data.length === 0) return [];
    const pages = [];
    pages.push(data.slice(0, 4)); // First page
    const remaining = data.slice(4);
    for (let i = 0; i < remaining.length; i += 5) {
      pages.push(remaining.slice(i, i + 5)); // Continuation pages
    }
    return pages;
  };

  const handleDownload = async () => {
    if (!users || users.length === 0) return;
    setIsGenerating(true);
    setProgress(0);

    const pdf = new jsPDF("p", "mm", "a4");
    const pageElements = document.querySelectorAll(".pdf-page-render");
    
    // Dynamic Filename based on Polling Unit
    const puName = filters.pollingUnit && filters.pollingUnit !== "All Polling Units" 
      ? filters.pollingUnit 
      : (users[0]?.pollingUnit || "Membership_Report");
    const fileName = `${puName.replace(/[/\\?%*:|"<> ]/g, "_")}.pdf`;

    try {
      for (let i = 0; i < pageElements.length; i++) {
        setProgress(Math.round(((i + 1) / pageElements.length) * 100));

        const canvas = await html2canvas(pageElements[i], {
          scale: 1.5,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.60);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        canvas.remove();
      }

      pdf.save(fileName);
    } catch (error) {
      console.error("PDF Error:", error);
      alert("Error generating PDF.");
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };


  const userPages = getPages(users);
  const now = new Date();

  const displayState = filters.state && filters.state !== "All States" ? filters.state : "ALL STATES";
  const displayLga = filters.lga && filters.lga !== "All LGAs" ? filters.lga : "ALL LGAs";
  const displayWard = filters.ward && filters.ward !== "All Wards" ? filters.ward : "ALL WARDS";
  const displayPu = filters.pollingUnit && filters.pollingUnit !== "All Polling Units" ? filters.pollingUnit : "ALL POLLING UNITS";

  return (
    <>
      <button
        className="btn btn-outline-danger w-100 btn-sm fw-bold py-2 shadow-sm d-flex align-items-center justify-content-center gap-2 mt-2"
        onClick={handleDownload}
        disabled={isGenerating || users.length === 0}
      >
        {isGenerating ? (
          <><span className="spinner-border spinner-border-sm" /> {progress}% ...</>
        ) : (
          <><FaFilePdf /> Download {displayPu} Report</>
        )}
      </button>

      {/* Hidden PDF Container */}
      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div id="report-print-container" style={{ width: "210mm", backgroundColor: "#fff" }}>
          {userPages.map((chunk, pageIndex) => (
            <div 
              key={pageIndex} 
              className="pdf-page-render"
              style={{
                width: "210mm",
                height: "297mm",
                padding: "8mm 12mm",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
              }}
            >
              {/* PAGE 1 HEADER - SPECIFIC FORMAT */}
              {pageIndex === 0 ? (
                <div style={{ WebkitPrintColorAdjust: "exact", marginBottom: "10px" }}>
                  <div style={{ textAlign: "center", marginBottom: "12px" }}>
                    <img src="/logo2.jpeg" alt="Logo" style={{ width: "22mm", height: "auto" }} />
                    <h2 style={{ margin: "2px 0", color: "#000", fontWeight: "bold", fontSize: "20px" }}>LABOUR PARTY (LP)</h2>
                    <h5 style={{ margin: "0", color: "#088d01", fontWeight: "bold", fontStyle: "italic", fontSize: "11px" }}>
                      Motto: Equal Opportunity and Social Justice
                    </h5>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#000", borderTop: "2px solid #088d01", paddingTop: "8px" }}>
                    <div style={{ lineHeight: "1.8" }}>
                      <div><strong>{displayState}</strong></div>
                      <div><strong>{displayLga}</strong></div>
                      <div><strong>{displayWard}</strong></div>
                      <div><strong>{displayPu}</strong></div>
                    </div>
                    <div style={{ textAlign: "right", lineHeight: "1.8" }}>
                      <div><strong>Total Record:</strong> {users.length}</div>
                      <div><strong>Time:</strong> {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      <div><strong>Date:</strong> {now.toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              ) : (
                /* CONTINUATION HEADER */
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "1px solid #088d01", paddingBottom: "4px", marginBottom: "10px", fontSize: "10px" }}>
                  {/* <img src="/logo2.jpeg" style={{ width: "10mm" }} alt="small-logo" /> */}
                  <span style={{ fontWeight: "bold" }}>DIRECTORY CONTINUATION — {displayWard} | {displayPu}</span>
                  <span>Page {pageIndex + 1}</span>
                </div>
              )}

              {/* RECORD LIST */}
              <div style={{ flexGrow: 1 }}>
                {chunk.map((user, i) => (
                  <div key={i} style={{ 
                    display: "flex", 
                    borderBottom: "2px solid #393838", 
                    padding: "8px 0", 
                    height: pageIndex === 0 ? "58mm" : "51mm", // Slight adjustment for continuation pages
                    alignItems: "center" 
                  }}>
                    {/* Photograph */}
                    <div style={{ width: "38mm", paddingRight: "15px", textAlign: "center" }}>
                      <img 
                        src={user.passportUrl || "/default-avatar.png"} 
                        alt="Photo" 
                        style={{ width: "35mm", height: "35mm", objectFit: "cover", borderRadius: "4px", border: "1px solid #505050" }}
                        crossOrigin="anonymous"
                      />
                      <div style={{ marginTop: "4px", fontWeight: "bold", color: "#d32f2f", fontSize: "11px" }}>
                        LP/{user.regID}
                      </div>
                    </div>

                    {/* Aligned User Data Grid */}
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 15px", fontSize: "10.5px" }}>
                      <div style={{ gridColumn: "span 2", marginBottom: "3px" }}>
                        <span style={{ color: "#121212", fontSize: "10px", textTransform: "uppercase" }}>Name</span><br/>
                        <strong style={{ fontSize: "14px", color: "#088d01" }}>{`${user.firstName} ${user.middleName || ''} ${user.lastName}`}</strong>
                      </div>
                      
                      <div><span style={{ color: "", fontSize: "10px" }}>SEX:</span> <strong>{user.gender || "N/A"}</strong></div>
                        
                      <div><span style={{ color: "#121212", fontSize: "10px" }}>DOB:</span> <strong>{user.dob ? new Date(user.dob).toLocaleDateString() : "N/A"}</strong></div>
                      
                      <div style={{ gridColumn: "span 2" }}>
                        <span style={{ color: "#121212", fontSize: "10px" }}>ADDRESS:</span><br/>
                        <strong style={{ fontSize: "10px" }}>{user.residentialAddress}</strong>
                      </div>

                      <div><span style={{ color: "#121212", fontSize: "10px" }}>STATE:</span> <strong>{user.state}</strong></div>
                      <div><span style={{ color: "#121212", fontSize: "10px" }}>LGA:</span> <strong>{user.lga}</strong></div>
                      
                      <div><span style={{ color: "#121212", fontSize: "10px" }}>WARD:</span> <strong>{user.ward}</strong></div>
                      <div><span style={{ color: "#121212", fontSize: "10px" }}>POLLING UNIT:</span> <strong>{user.pollingUnit}</strong></div>
                      
                      <div style={{ gridColumn: "span 2" }}>
                        <span style={{ color: "#121212", fontSize: "10px" }}>NIN:</span> <strong>{user.nationalId || "N/A"}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* FOOTER */}
              <div style={{ textAlign: "center", padding: "4px 0", fontSize: "8px", color: "#aaa", borderTop: "1px solid #eee" }}>
                Official Membership Directory — Page {pageIndex + 1} of {userPages.length}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ReportDownload;